// Typed fetch wrapper for the APM REST API.
//
// - Always sends cookies (FastAPI session auth).
// - Throws ApiError with status + body on non-2xx.
// - On 401, invokes the global onUnauthorized handler so the app can redirect
//   to /login without every caller having to do it.

import type { ZodTypeAny, infer as ZodInfer } from 'zod';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

export interface RequestOptions<S extends ZodTypeAny | undefined = undefined> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  schema?: S;
  // Set to true to skip the 401 redirect side effect (e.g. for the /me probe
  // we do at boot to detect whether the user is already signed in).
  skipAuthRedirect?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function apiRequest<S extends ZodTypeAny | undefined = undefined>(
  path: string,
  options: RequestOptions<S> = {},
): Promise<S extends ZodTypeAny ? ZodInfer<S> : unknown> {
  const { method = 'GET', body, query, schema, skipAuthRedirect } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  let serialized: BodyInit | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    serialized = JSON.stringify(body);
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',
    body: serialized,
  });

  // Pull the body once. JSON if we can; otherwise plain text.
  const ctype = res.headers.get('content-type') ?? '';
  const payload: unknown = ctype.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    if (res.status === 401 && !skipAuthRedirect && onUnauthorized) {
      onUnauthorized();
    }
    const detail =
      payload && typeof payload === 'object' && 'detail' in payload
        ? String((payload as { detail: unknown }).detail)
        : res.statusText;
    throw new ApiError(`${res.status} ${detail}`, res.status, payload);
  }

  if (!schema) {
    return payload as S extends ZodTypeAny ? ZodInfer<S> : unknown;
  }
  return schema.parse(payload) as S extends ZodTypeAny ? ZodInfer<S> : unknown;
}
